import { zodResolver } from "@hookform/resolvers/zod";
import type { InviteDto } from "@ied/shared-types";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { useRegisterAdmin } from "@/hooks/mutations/use-register-admin";
import { Button } from "../ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const FormSchema = z
	.object({
		name: z.string().nonempty("Name is required"),
		email: z.string().email("Invalid email address"),
		password: z.string().min(8, "Password must be at least 8 characters"),
		passwordConfirm: z.string(),
	})
	.refine((data) => data.password === data.passwordConfirm, {
		message: "Passwords don't match",
		path: ["passwordConfirm"],
	});

type AdminRegisterFormType = z.infer<typeof FormSchema>;

type Props = {
	onSuccess: () => void;
	onError: () => void;
	invite: InviteDto | undefined;
};

export function AdminRegisterForm({ onSuccess, onError, invite }: Props) {
	const { mutate: registerAdmin } = useRegisterAdmin();
	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm<AdminRegisterFormType>({
		defaultValues: {
			name: "",
			email: "",
			password: "",
			passwordConfirm: "",
		},
		resolver: zodResolver(FormSchema),
	});

	const onSubmit: SubmitHandler<AdminRegisterFormType> = (data) => {
		if (!invite) {
			onError();
			return;
		}

		registerAdmin(
			{
				...data,
				inviteCode: invite.invite_code,
			},
			{
				onSuccess,
				onError,
			},
		);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Card className="relative w-full max-w-sm overflow-hidden pt-0">
				<div className="bg-primary absolute inset-0 z-30 aspect-video opacity-50 mix-blend-color" />
				<img
					src="https://images.unsplash.com/photo-1604076850742-4c7221f3101b?q=80&w=1887&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					className="relative z-20 aspect-video w-full object-cover brightness-60 grayscale"
				/>
				<CardHeader className="space-y-4">
					<CardTitle className="text-lg">
						Admin Registration - INDIVISIBLE El Dorado
					</CardTitle>
				</CardHeader>
				<CardDescription className="px-4 space-y-1">
					<p className="text-base">Create your admin account</p>
					<p>
						Please provide the requested information to create your admin
						account for the INDIVISIBLE El Dorado admin dashboard.
					</p>
					<div className="space-y-4 mt-6">
						<div className="space-y-2">
							<Label className="font-bold" htmlFor="name">
								Full Name
							</Label>
							<Input {...register("name")} id="name" autoFocus />
							{errors.name && (
								<p className="text-red-500 text-xs">{errors.name.message}</p>
							)}
						</div>
						<div className="space-y-2">
							<Label className="font-bold" htmlFor="email">
								Email
							</Label>
							<Input {...register("email")} type="email" id="email" />
							{errors.email && (
								<p className="text-red-500 text-xs">{errors.email.message}</p>
							)}
						</div>
						<div className="space-y-2">
							<Label className="font-bold" htmlFor="password">
								Password
							</Label>
							<Input {...register("password")} type="password" id="password" />
							{errors.password && (
								<p className="text-red-500 text-xs">
									{errors.password.message}
								</p>
							)}
						</div>
						<div className="space-y-2">
							<Label className="font-bold" htmlFor="passwordConfirm">
								Confirm Password
							</Label>
							<Input
								{...register("passwordConfirm")}
								type="password"
								id="passwordConfirm"
							/>
							{errors.passwordConfirm && (
								<p className="text-red-500 text-xs">
									{errors.passwordConfirm.message}
								</p>
							)}
						</div>
					</div>
				</CardDescription>
				<CardFooter className="flex justify-between">
					<Button type="submit">Create Account</Button>
				</CardFooter>
			</Card>
		</form>
	);
}
